import {
  Delete as DeleteIcon,
  Label as LabelIcon,
  MoreVert as MoreVertIcon,
  DriveFileRenameOutline as RenameIcon,
} from '@mui/icons-material'
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material'
import React, { useState } from 'react'

import { ContentFile } from '../services/api'

interface ChapterContextMenuProps {
  chapter: ContentFile
  onDelete: (chapter: ContentFile) => void
  onRename?: (chapter: ContentFile) => void
  onTag?: (chapter: ContentFile) => void
}

const ChapterContextMenu: React.FC<ChapterContextMenuProps> = ({ chapter, onDelete, onRename, onTag }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (event?: React.MouseEvent | object, _reason?: 'backdropClick' | 'escapeKeyDown') => {
    if (event && 'stopPropagation' in event) {
      event.stopPropagation()
    }
    setAnchorEl(null)
  }

  const handleDelete = (event: React.MouseEvent) => {
    event.stopPropagation()
    handleClose()
    onDelete(chapter)
  }

  const handleRename = (event: React.MouseEvent) => {
    event.stopPropagation()
    handleClose()
    if (onRename) {
      onRename(chapter)
    }
  }

  const handleTag = (event: React.MouseEvent) => {
    event.stopPropagation()
    handleClose()
    if (onTag) {
      onTag(chapter)
    }
  }

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        sx={{
          opacity: 0,
          transition: 'opacity 0.2s ease-in-out',
          '.MuiBox-root:hover &': { opacity: 1 },
          color: 'var(--menu-icon)',
          width: 24,
          height: 24,
          '&:hover': {
            color: 'var(--menu-icon-hover)',
            bgcolor: 'var(--bg-hover)',
            transform: 'scale(1.1)',
          },
        }}
        aria-label="Source file options"
      >
        <MoreVertIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            boxShadow: '0px 4px 20px var(--menu-shadow)',
            border: '1px solid var(--menu-border)',
            borderRadius: 2,
            minWidth: 160,
            '& .MuiMenuItem-root': {
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
              fontSize: '14px',
              py: 1.5,
              px: 2,
              borderRadius: 1,
              mx: 0.5,
              '&:hover': {
                bgcolor: 'var(--bg-hover)',
              },
            },
          },
        }}
      >
        {onTag && (
          <MenuItem onClick={handleTag}>
            <ListItemIcon>
              <LabelIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Tag Source File</ListItemText>
          </MenuItem>
        )}
        {onRename && (
          <MenuItem onClick={handleRename}>
            <ListItemIcon>
              <RenameIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Rename Source File</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={handleDelete}
          sx={{
            color: 'error.main',
            '& .MuiListItemIcon-root': {
              color: 'error.main',
            },
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete Source File</ListItemText>
        </MenuItem>
      </Menu>
    </>
  )
}

export default ChapterContextMenu
